import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormPageShell } from '../../components/DataTablePanel';
import { FinancialLinesEditor } from '../../components/FinancialLinesEditor';
import {
  Button,
  Callout,
  Card,
  Label,
  LoadingSpinner,
  NumberInput,
  SaveIcon,
  Select,
  Switch,
  TextInput,
  Textarea,
  XIcon,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { t } from '../../i18n';
import { GREEK_PREFECTURES } from './greekPrefectures';
import {
  AccountingBooksCategory,
  CustomerAssignee,
} from '../../types/customer';
import { Office } from '../../types/office';
import {
  EditableFinancialLine,
  amountToApiString,
  createEmptyLine,
  parseAmountInput,
} from '../../utils/financialLines';

type FormState = {
  name: string;
  vatNumber: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  accountingBooksCategory: string;
  prefecture: string;
  officeId: string;
  assignedUserId: string;
  creditDays: number;
  active: boolean;
  agreementNotes: string;
  lines: EditableFinancialLine[];
};

const emptyForm = (): FormState => ({
  name: '',
  vatNumber: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
  accountingBooksCategory: '',
  prefecture: '',
  officeId: '',
  assignedUserId: '',
  creditDays: 0,
  active: true,
  agreementNotes: '',
  lines: [createEmptyLine('INVOICE')],
});

export function CustomerFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const labels = t().customers;
  const canEditAgreements = Boolean(user?.features?.includes('agreements'));

  const [form, setForm] = useState<FormState>(emptyForm);
  const [assignees, setAssignees] = useState<CustomerAssignee[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadAssignees() {
      try {
        const [users, officeList] = await Promise.all([
          api.getCustomerAssignees(),
          api.getOffices(false),
        ]);
        if (!cancelled) {
          setAssignees(users);
          setOffices(officeList);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      }
    }

    void loadAssignees();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const customer = await api.getCustomer(Number(id));
        if (cancelled) return;
        setForm({
          name: customer.name,
          vatNumber: customer.vatNumber ?? '',
          email: customer.email ?? '',
          phone: customer.phone ?? '',
          address: customer.address ?? '',
          notes: customer.notes ?? '',
          accountingBooksCategory: customer.accountingBooksCategory ?? '',
          prefecture: customer.prefecture ?? '',
          officeId: customer.office ? String(customer.office.id) : '',
          assignedUserId: customer.assignedUser
            ? String(customer.assignedUser.id)
            : '',
          creditDays: customer.creditDays ?? 0,
          active: customer.active,
          agreementNotes: customer.agreement?.notes ?? '',
          lines:
            customer.agreement?.lines.length
              ? customer.agreement.lines.map((line) => ({
                  key: `line-${line.id ?? Math.random()}`,
                  type: line.type,
                  amount: line.amount,
                  description: line.description ?? '',
                }))
              : [createEmptyLine('INVOICE')],
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError(labels.requiredName);
      return;
    }

    const validLines = canEditAgreements
      ? form.lines.filter((line) => {
          return line.amount.trim() !== '' || line.description.trim() !== '';
        })
      : [];

    for (const line of validLines) {
      const amount = parseAmountInput(line.amount);
      if (!line.amount.trim() || !Number.isFinite(amount) || amount <= 0) {
        setError(t().common.amountMustBePositive);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        vatNumber: form.vatNumber.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        notes: form.notes.trim() || undefined,
        accountingBooksCategory: form.accountingBooksCategory
          ? (form.accountingBooksCategory as AccountingBooksCategory)
          : null,
        prefecture: form.prefecture || null,
        officeId: form.officeId ? Number(form.officeId) : null,
        assignedUserId: form.assignedUserId
          ? Number(form.assignedUserId)
          : null,
        creditDays: form.creditDays,
        active: form.active,
        ...(canEditAgreements
          ? {
              agreement: {
                notes: form.agreementNotes.trim() || undefined,
                lines: validLines.map((line, index) => ({
                  type: line.type,
                  amount: amountToApiString(line.amount),
                  description: line.description.trim() || undefined,
                  sortOrder: index,
                })),
              },
            }
          : {}),
      };

      const saved = isEdit
        ? await api.updateCustomer(Number(id), payload)
        : await api.createCustomer(payload);

      navigate(`/customers/${saved.id}`);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : t().toasts.saveError,
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingSpinner message={t().common.loading} size="small" />;
  }

  return (
    <FormPageShell
      onSubmit={handleSubmit}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate(isEdit ? `/customers/${id}` : '/customers')
            }
          >
            <XIcon className="h-4 w-4" />
            {t().common.cancel}
          </Button>
          <Button type="submit" loading={saving}>
            <SaveIcon className="h-4 w-4" />
            {t().common.save}
          </Button>
        </>
      }
    >
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-ink">
          {isEdit ? labels.edit : labels.new}
        </h2>
        <Link to="/customers" className="text-sm text-brand hover:underline">
          {t().common.backToList}
        </Link>
      </div>

      {error ? <Callout variant="danger">{error}</Callout> : null}

      <Card className="space-y-4 p-5">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label required>{labels.name}</Label>
            <TextInput
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              showClearButton={false}
              placeholder=""
            />
          </div>
          <div>
            <Label>{labels.vatNumber}</Label>
            <TextInput
              value={form.vatNumber}
              onChange={(e) => updateField('vatNumber', e.target.value)}
              showClearButton={false}
              placeholder=""
            />
          </div>
          <div>
            <Label>{labels.email}</Label>
            <TextInput
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              showClearButton={false}
              placeholder=""
            />
          </div>
          <div>
            <Label>{labels.phone}</Label>
            <TextInput
              value={form.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              showClearButton={false}
              placeholder=""
            />
          </div>
          <div>
            <Label>{labels.address}</Label>
            <TextInput
              value={form.address}
              onChange={(e) => updateField('address', e.target.value)}
              showClearButton={false}
              placeholder=""
            />
          </div>
          <div>
            <Label>{labels.accountingBooksCategory}</Label>
            <Select
              value={form.accountingBooksCategory}
              options={[
                {
                  value: 'B_SINGLE_ENTRY',
                  label: labels.booksSingleEntry,
                },
                {
                  value: 'C_DOUBLE_ENTRY',
                  label: labels.booksDoubleEntry,
                },
              ]}
              placeholder={t().common.placeholders.select}
              showClearButton
              onChange={(e) =>
                updateField('accountingBooksCategory', e.target.value)
              }
            />
          </div>
          <div>
            <Label>{labels.prefecture}</Label>
            <Select
              value={form.prefecture}
              options={GREEK_PREFECTURES.map((name) => ({
                value: name,
                label: name,
              }))}
              placeholder={t().common.placeholders.select}
              showClearButton
              onChange={(e) => updateField('prefecture', e.target.value)}
            />
          </div>
          <div>
            <Label>{labels.creditDays}</Label>
            <NumberInput
              value={form.creditDays}
              allowDecimals={false}
              min={0}
              max={3650}
              showClearButton={false}
              onChange={(e) =>
                updateField('creditDays', e.target.value ?? 0)
              }
            />
          </div>
          <div>
            <Label>{labels.assignedUser}</Label>
            <Select
              value={form.assignedUserId}
              options={assignees
                .filter((user) => {
                  const selected = String(user.id) === form.assignedUserId;
                  return user.active || selected;
                })
                .map((user) => ({
                  value: String(user.id),
                  label: user.active
                    ? user.name
                    : `${user.name} (${labels.inactive})`,
                  description: user.office?.name,
                }))}
              placeholder={t().common.placeholders.select}
              showClearButton
              onChange={(e) => updateField('assignedUserId', e.target.value)}
            />
          </div>
          <div>
            <Label>{labels.responsibleOffice}</Label>
            <Select
              value={form.officeId}
              options={offices
                .filter(
                  (office) =>
                    office.active || String(office.id) === form.officeId,
                )
                .map((office) => ({
                  value: String(office.id),
                  label: office.active
                    ? office.name
                    : `${office.name} (${labels.inactive})`,
                }))}
              placeholder={t().common.placeholders.select}
              showClearButton
              onChange={(e) => updateField('officeId', e.target.value)}
            />
          </div>
          <div>
            <Label>{labels.status}</Label>
            <Switch
              checked={form.active}
              onChange={(e) => updateField('active', e.target.checked)}
              label={labels.active}
            />
          </div>
        </div>
        <div>
          <Label>{labels.notes}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={3}
          />
        </div>
      </Card>

      {canEditAgreements ? (
        <Card className="space-y-4 p-5">
          <h3 className="text-sm font-semibold text-ink">
            {labels.agreementSection}
          </h3>
          <div>
            <Label>{labels.agreementNotes}</Label>
            <Textarea
              value={form.agreementNotes}
              onChange={(e) => updateField('agreementNotes', e.target.value)}
              rows={2}
            />
          </div>
          <FinancialLinesEditor
            lines={form.lines}
            onChange={(lines) => updateField('lines', lines)}
            disabled={saving}
          />
        </Card>
      ) : null}
    </FormPageShell>
  );
}
